import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useDaoConfig } from '@/hooks/useDaoConfig';

import { animateElement, type AnimationType } from './../animations';
import type { WelcomeStepRef } from './types';

export const WelcomeStep3 = forwardRef<WelcomeStepRef>((_, ref) => {
  const { clientRole, workerRolePlural, coordinatorRole } = useDaoConfig();
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const elements = containerRef.current?.querySelectorAll<HTMLElement>('[data-animate-in]');

    elements?.forEach((element) => {
      const animation = element.dataset.animateIn as AnimationType | undefined;

      if (animation) {
        animateElement(element, animation).catch(() => {});
      }
    });
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      animateOut: async () => {
        const elements = containerRef.current?.querySelectorAll<HTMLElement>('[data-animate-out]');

        if (!elements) return;

        await Promise.all(
          Array.from(elements).map((element) => {
            const animation = element.dataset.animateOut as AnimationType | undefined;

            if (!animation) return Promise.resolve();

            return animateElement(element, animation);
          }),
        );
      },
    }),
    [],
  );

  return (
    <div ref={containerRef} className="stepContainer">
      <h1 data-animate-in="textFadeIn" data-animate-out="textFadeOut" className="opacity-0">
        How matches are made.
      </h1>

      <p
        data-animate-in="textFadeIn"
        data-animate-out="textFadeOut"
        className="opacity-0 text-[--text-dark-secondary] font-normal"
      >
        Every project starts with a {coordinatorRole.toLowerCase()} who prepares the plan. When the{' '}
        {clientRole.toLowerCase()} approves execution, qualified {workerRolePlural.toLowerCase()} are matched to the plan and
        their availability.
      </p>

      {/* Image - animations container */}
      <div className="animationsContainer">
        <div className="relative translate-y-4" data-animate-out="fadeOut">
          <div className="h-[167px] scale-[1.2] aspect-square rounded-full glassEffect ">
            {/* bg-[--base-surface-1] bg-opacity-5 */}
            <img
              src="./public/images/onboarding/gradientBorder_circle.svg"
              // alt="gradient border circle"
            />
          </div>

          {/* Blue bubble - top left */}
          <div
            className="
              z-50 absolute 
              top-[16px] left-[16px] -translate-x-1/2 -translate-y-1/2
              h-[37px] aspect-square max-w-[37px]
              rounded-full bg-[--colors-cyan-600]
            "
          />

          {/* Green bubble - bottom right */}
          <div
            className="
              z-50 absolute 
              top-[180px] left-1/2 -translate-x-1/2 -translate-y-1/2
              h-[18px] aspect-square
            "
          >
            <div
              className="relative h-full flex items-center justify-start overflow-visible opacity-0"
              data-animate-in="fadeIn"
              data-animate-out="fadeOut"
            >
              {/* BACK PANEL */}
              <div
                className="
                absolute -top-2 left-1/2 -translate-x-1/2 
                p-4 w-fit flex flex-col gap-3
                rounded-[--radi-8] border border-[--base-border] 
                largeShadow_black glassEffect bg-[--base-surface-1]
                "
              >
                {/* row 1 */}
                <div className="row mt-[42px] pt-4 flex gap-4">
                  <span className="h-[24px] aspect-square rounded-full border border-dashed border-[--colors-emerald-500]" />
                  <span className="h-[24px] aspect-square rounded-full border border-dashed border-[--colors-emerald-500]" />
                  <span className="h-[24px] aspect-square rounded-full border border-dashed border-[--colors-emerald-500]" />
                </div>
                {/* row 2 */}
                <div className="row flex gap-4">
                  <span className="h-[24px] aspect-square rounded-full bg-[--colors-emerald-500]" />
                  <span className="h-[24px] aspect-square rounded-full bg-[--colors-emerald-500]" />
                  <span className="h-[24px] aspect-square rounded-full bg-[--colors-emerald-500]" />
                </div>
                {/* row 3 */}
                <div className="row flex gap-4">
                  <span className="h-[24px] aspect-square rounded-full bg-[--colors-emerald-500]" />
                  <span className="h-[24px] aspect-square rounded-full bg-[--colors-emerald-500]" />
                  <span className="h-[24px] aspect-square rounded-full bg-[--colors-emerald-500]" />
                </div>
              </div>

              {/* INLINE DOTS */}
              <div
                className="
                absolute top-1/2 left-1/2 -translate-x-1/2 
                py-[11px] px-[--spacing-8] flex items-center gap-[24px] 
                rounded-[--radi-7] glassEffect bg-[--base-surface-1]
              "
              >
                {/* Red dots */}
                <ul className="flex items-center gap-[8px] [&>li]:h-[18px] [&>li]:aspect-square [&>li]:rounded-full">
                  <li className="bg-[color-mix(in_srgb,var(--colors-red-200)_10%,transparent)]"></li>
                  <li className="bg-[color-mix(in_srgb,var(--colors-red-200)_20%,transparent)]"></li>
                  <li className="bg-[color-mix(in_srgb,var(--colors-red-200)_30%,transparent)]"></li>
                  <li className="bg-[color-mix(in_srgb,var(--colors-red-200)_67%,transparent)]"></li>
                </ul>
                {/* Green central dot */}
                <div className="relative [&>span]:absolute [&>span]:top-1/2 [&>span]:left-[1/2] [&>span]:-translate-y-1/2 [&>span]:-translate-x-1/2">
                  <span className="h-[34px] aspect-square rounded-full bg-white opacity-10" />
                  <span className="h-[18px] aspect-square rounded-full bg-[--colors-emerald-500]" />
                </div>
                {/* Green dots */}
                <ul className="flex items-center gap-[8px] [&>li]:h-[18px] [&>li]:aspect-square [&>li]:rounded-full">
                  <li className="bg-[color-mix(in_srgb,var(--colors-emerald-500)_67%,transparent)]"></li>
                  <li className="bg-[color-mix(in_srgb,var(--colors-emerald-500)_30%,transparent)]"></li>
                  <li className="bg-[color-mix(in_srgb,var(--colors-emerald-500)_20%,transparent)]"></li>
                  <li className="bg-[color-mix(in_srgb,var(--colors-emerald-500)_10%,transparent)]"></li>
                </ul>
              </div>
            </div>
          </div>

          {/* Purple bubble - center */}
          <div
            className="
              z-50 absolute 
              top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
              px-[--spacing-8] py-[--spacing-5]
              h-[37px] aspect-square max-w-[37px]
              rounded-full bg-[--colors-fuchsia-400]
              border border-[--colors-alpha-dark-200]
            "
          />
        </div>
      </div>
      <p
        data-animate-in="textFadeIn"
        data-animate-out="textFadeOut"
        className="mt-16 opacity-0 text-[--text-dark-secondary] font-normal"
      >
        No bidding wars. No guesswork.
      </p>
    </div>
  );
});

WelcomeStep3.displayName = 'WelcomeStep3';
